<template>
  <div
    class="sf-banner"
    :class="outerContainer"
    v-bind="$attrs"
    @click.stop.prevent="handleClick"
  >
    <span
      v-if="showIcon"
      class="sf-banner-icon fa tw-leading-[18px]"
      :class="effectiveIconClass"
    />
    <span class="sf-banner-title tw-leading-[18px]">
      {{ title }}
      <slot name="title" />
    </span>
    <div v-if="hasActions" class="sf-banner-right">
      <div class="sf-banner-actions">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { FlexAlign } from '@app/shared/constants'
import type { FlexAlignType } from '@app/shared/constants'

import { bannerVariants } from '@app/shared/types/banner'
import type { BannerVariant } from '@app/shared/types/banner'

import { computed, useSlots } from 'vue'

import type { BannerIconType } from './constants'
import { icons } from './constants'

defineOptions({
  name: 'SfBannerInline',
})

const slots = useSlots()

const emit = defineEmits(['click'])

interface Props {
  align?: Extract<FlexAlignType, 'start' | 'center'>
  textAlign?: Extract<FlexAlignType, 'start' | 'center'>
  iconClass?: BannerIconType
  title?: string
  variant: BannerVariant
  showIcon?: boolean
  hoverable?: boolean
  isFullWidth?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  align: FlexAlign.CENTER,
  textAlign: FlexAlign.START,
  iconClass: '',
  title: '',
  variant: bannerVariants.info,
  showIcon: true,
  hoverable: false,
  isFullWidth: false,
})

const effectiveIconClass = computed(() => {
  if (props.iconClass) {
    return props.iconClass
  }

  return icons[props.variant]
})

const hasActions = computed(() => {
  return !!slots.actions
})

const outerContainer = computed(() => {
  const effectiveClass = `sf-banner-inline-${props.variant}`
  return {
    [effectiveClass]: true,
    [`${effectiveClass}--hoverable`]: props.hoverable,
    [`${effectiveClass}--full-width`]: props.isFullWidth,
    'tw-items-center': props.align === FlexAlign.CENTER,
    'tw-items-start': props.align === FlexAlign.START,
    'tw-justify-center': props.textAlign === FlexAlign.CENTER,
    'tw-justify-start': props.textAlign === FlexAlign.START,
  }
})

function handleClick(e: Event): void {
  emit('click', e)
}
</script>
